/* eslint-disable no-underscore-dangle */

/**
 * This service handles uploads to Amazon S3 buckets.
 * Please see 'docs/request-examples/uploaderRelatedRequests.js' for more info on the different api requests involved.
 * The bucket name is based on the current environment, eg 'insperalearning-local' or 'insperalearning-prod'.
 * The awsSignatureVersion = 2.
 */

import Evaporate from 'evaporate';
import SparkMD5 from 'spark-md5';

import { reportError } from '../ErrorReporting';
import apiService from '../api/ApiService';
import globalsService from '../GlobalsService';
import navigationService from '../NavigationService';
import storeManager from '../../store/StoreManager';
import timeReader from '../../store/readHelpers/TimeReader';
import uploadTokenActions from '../../actions/UploadTokenActions';

import { t } from '../i18nService';

let instance;

class UploadService {
    static getInstance() {
        if (instance) {
            return Promise.resolve(instance);
        }

        return apiService.getUploaderInfo()
            .then((config) => {
                const uploaderToken = globalsService.getUploaderToken();
                const evaporateInstance = new Evaporate({
                    _token: uploaderToken,
                    aws_key: config.accessKey,
                    aws_url: config.awsUrl,
                    awsRegion: 'eu-west-1',
                    awsSignatureVersion: 2,
                    bucket: config.bucket,
                    partSize: 6 * 1024 * 1024,
                    maxConcurrentParts: 5,
                    computeContentMd5: true,
                    cryptoMd5Method(data) {
                        return btoa(SparkMD5.ArrayBuffer.hash(data, true));
                    },
                    signerUrl: '/ICSXapi/assessment/plain/SignURL',
                    timeUrl: '/ICSXapi/assessment/plain/GetServerTime',
                });

                instance = new UploadService(evaporateInstance, uploaderToken);

                return instance;
            });
    }

    static setInstance(newInstance) {
        instance = newInstance;
    }

    static uploadAssignmentFile(file, questionId, contentRevisionId, callback, skipContentUrlCreation = false) {
        const state = storeManager.getState();
        const assessmentRunContentId = state.assessment.assessmentRunContentId;
        let statusCallback = callback;
        let fileKey;
        let uploaderInstance;
        let cancelUpload = false;
        let ticket;

        function handleCallback(type, forFile, payload) {
            if (statusCallback === undefined) {
                return false;
            }
            if (type === 'complete') {
                apiService.sendUploadSuccess(
                    ticket.ticket,
                    questionId,
                    assessmentRunContentId,
                    uploaderInstance.uploaderToken,
                    skipContentUrlCreation
                )
                    .then((response) => {
                        if (response.uploadToken !== undefined) {
                            storeManager.dispatch(
                                uploadTokenActions.setUploadToken(contentRevisionId, response.uploadToken)
                            );
                        }

                        if (timeReader.timeHasExpired()) {
                            setTimeout(() => {
                                navigationService.navigateToDeliveringPage();
                            }, 10000);
                        }

                        if (response.FileNotFoundOnS3) {
                            const errorText = t('interactions.upload.error.FileNotFoundOnS3');
                            reportError(errorText);
                            statusCallback('error', forFile, errorText, Date.now());
                        } else {
                            const payload = skipContentUrlCreation ? response : response.contentRevision;
                            statusCallback('sent-upload-success', forFile, payload, Date.now());
                        }
                    })
                    .catch((error) => {
                        reportError(error);
                        statusCallback('error', forFile, error, Date.now());
                    });
            }

            return statusCallback(type, forFile, payload, Date.now());
        }

        UploadService.getInstance()
            .then((uploader) => {
                uploaderInstance = uploader;
                return apiService.getUploadTicket(
                    file.type,
                    file.name,
                    file.size,
                    questionId,
                    assessmentRunContentId,
                    uploader.uploaderToken,
                );
            })
            .then((ticketData) => {
                if (cancelUpload) { // In case cancel is called while the ticketData is being fetched.
                    return statusCallback('cancelled', file);
                }

                ticket = ticketData;
                fileKey = `${uploaderInstance.evaporateInstance.config.bucket}/${ticketData.key}`;
                return uploaderInstance.uploadFile(
                    ticketData.key,
                    file,
                    ticketData.ticket,
                    (type, forFile, payload) => handleCallback(type, forFile, payload),
                );
            })
            .catch((error) => {
                if (error === 'User aborted the upload') {
                    return;
                }

                reportError(error);
                statusCallback('error', file, error);
            });

        return {
            cancel() {
                if (uploaderInstance) {
                    uploaderInstance.evaporateInstance.cancel(fileKey);
                }

                cancelUpload = true;
            },

            stopListening() {
                statusCallback = undefined;
            },
        };
    }

    constructor(evaporateInstance, uploaderToken) {
        this.evaporateInstance = evaporateInstance;
        this.uploaderToken = uploaderToken;
    }

    getUploadTicketForFile(file, questionId, assessmentRunContentId) {
        return apiService.getUploadTicket(file.type, file.name, file.size, questionId, assessmentRunContentId, this.uploaderToken);
    }

    uploadFile(s3FileName, file, ticket, callback) {
        const options = {
            name: s3FileName,
            file,
            contentType: file.type,
            notSignedHeadersAtInitiate: {
                'Cache-Control': 'max-age=3600',
            },
            xAmzHeadersAtInitiate: {
                'x-amz-acl': 'private',
            },
            started(uploadId) {
                callback('started', file, uploadId);
            },
            pausing(uploadId) {
                callback('pausing', file, uploadId);
            },
            paused(uploadId) {
                callback('paused', file, uploadId);
            },
            resumed(uploadId) {
                callback('resumed', file, uploadId);
            },
            cancelled() {
                callback('cancelled', file);
            },
            complete(xhr, awsObjectKey) {
                callback('complete', file, awsObjectKey);
            },
            progress(progress) {
                callback('progress', file, progress);
            },
            warn(message) {
                callback('warning', file, message);
            },
            error(message) {
                callback('error', file, message);
            },
            info(message) {
                callback('info', file, message);
            },
        };

        const overrideOptions = {
            signHeaders: {},

            signParams: {
                ticket,
                mimeType: file.type,
                fileSize: file.size,
            },

            beforeSigner(xhr) {
                const requestDate = new Date().toISOString();
                xhr.setRequestHeader('Request-Header', requestDate);
            },
        };

        return this.evaporateInstance.add(options, overrideOptions);
    }
}

export default UploadService;
